import { AlertTriangle, RefreshCw, ShieldCheck, Check, Users } from 'lucide-react';
import { SHEET_TOKENS, getTextColors, getInnerCardSurface } from './sheetStyle';

/**
 * Zámky uzávěrky — co brání spočítat pokladnu, a proč.
 *
 * Společné pro rozvozce i hlavní pokladnu, protože je to stejná otázka:
 * **jsou data, ze kterých se počítá, konečná?** Když se pokladna spočítá
 * uprostřed toho, jak do systému doteká zbytek dne, vyjde rozdíl, který
 * nikdo neudělal — a při dalším pokusu vyjde jiný. Proto se dřív ukáže
 * důvod než tlačítko.
 *
 * Dva druhy zámků:
 *   - **tvrdý** (rozpracovaná objednávka, kolega bez hotového uzavření
 *     směny) — nejde obejít, jde jen dořešit.
 *     Vypsané objednávky jsou zároveň seznam práce: dokud je někdo neuzavře
 *     nebo nestornuje, peníze za ně nejsou vybrané.
 *   - **přeskočitelný** (data ze serveru) — scraper Objedname se dlouho
 *     neozval. Nevíme, jestli něco nechybí, ale spadlý scraper nesmí nechat
 *     provozovnu přes noc otevřenou: manažer smí potvrdit i tak a zůstane
 *     to zapsané v poznámce uzávěrky.
 *
 * Když je čisto, zbyde jediný tichý řádek — kdy se data naposledy potvrdila
 * proti serveru. Je to jediné místo, kde se to dá zjistit, a stojí to za dva
 * řádky výšky: uzávěrka bez něj je slib bez krytí.
 */
export default function ZamkyUzaverky({
    zamky = [],
    neukoncene = [],
    kolegove = [],
    dataServer = null,
    cekaNaPlatbu = 0,
    prekrocit = false,
    onPrekrocit,
    onZnovu,
    nacitam = false,
    isDark,
}) {
    const barvy = getTextColors(isDark);
    const zamekObjednavky = zamky.find(z => z.kod === 'neukoncene_objednavky');
    const zamekData = zamky.find(z => z.kod === 'data_ze_serveru');
    const zamekKolegove = zamky.find(z => z.kod === 'kolegove');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Kolegové napřed: dokud nemají hotovo, chybí v pokladně jejich
                pohyby a kontrola by nevyšla. Seznam = komu zavolat. */}
            {zamekKolegove && (
                <div style={{
                    ...getInnerCardSurface(isDark),
                    borderColor: 'rgba(255,138,0,0.30)',
                    background: 'rgba(255,138,0,0.10)',
                    borderRadius: SHEET_TOKENS.radius.card,
                    padding: '10px 14px',
                    display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <Users size={15} color="#FF8A00" style={{ flexShrink: 0, marginTop: 1 }} />
                        <span style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.4, color: '#FF8A00' }}>
                            Kontrola se odemkne, až budou mít všichni hotovo — jinak v pokladně chybí jejich pohyby.
                        </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {kolegove.map(k => (
                            <div key={`${k.zamestnanec_id}-${k.kod}`} style={{ fontSize: 12.5, lineHeight: 1.4, color: barvy.textSecondary }}>
                                <strong style={{ color: barvy.textPrimary }}>{k.jmeno}</strong> — {k.text}
                            </div>
                        ))}
                    </div>
                    <div><TlacitkoZnovu onZnovu={onZnovu} nacitam={nacitam} isDark={isDark} /></div>
                </div>
            )}

            {zamekObjednavky && (
                <div style={{
                    ...getInnerCardSurface(isDark),
                    borderColor: 'rgba(255,138,0,0.30)',
                    background: 'rgba(255,138,0,0.10)',
                    borderRadius: SHEET_TOKENS.radius.card,
                    padding: '10px 14px',
                    display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <AlertTriangle size={15} color="#FF8A00" style={{ flexShrink: 0, marginTop: 1 }} />
                        <span style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.4, color: '#FF8A00' }}>
                            {zamekObjednavky.text}
                        </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {neukoncene.map(o => (
                            <div key={o.id} style={{
                                display: 'flex', alignItems: 'baseline', gap: 8,
                                fontSize: 12.5, color: barvy.textSecondary,
                            }}>
                                <strong style={{
                                    color: barvy.textPrimary, fontVariantNumeric: 'tabular-nums',
                                    minWidth: 44,
                                }}>
                                    {o.cislo ? `#${o.cislo}` : '—'}
                                </strong>
                                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {o.stav}
                                    {o.penezenka ? ` · ${o.penezenka}` : ''}
                                    {o.cas ? ` · ${o.cas}` : ''}
                                </span>
                                <span style={{ fontVariantNumeric: 'tabular-nums', color: barvy.textMuted, flexShrink: 0 }}>
                                    {Math.round(o.celkem_s_dph).toLocaleString('cs-CZ')} Kč
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {zamekData && (
                <div style={{
                    ...getInnerCardSurface(isDark),
                    borderColor: 'rgba(239,68,68,0.30)',
                    background: 'rgba(239,68,68,0.10)',
                    borderRadius: SHEET_TOKENS.radius.card,
                    padding: '10px 14px',
                    display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <AlertTriangle size={15} color={barvy.danger} style={{ flexShrink: 0, marginTop: 1 }} />
                        <span style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.4, color: barvy.danger }}>
                            {zamekData.text}
                        </span>
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <TlacitkoZnovu onZnovu={onZnovu} nacitam={nacitam} isDark={isDark} />
                        {onPrekrocit && (
                            <Prepinac
                                zapnuto={prekrocit}
                                onZmena={() => onPrekrocit(!prekrocit)}
                                popis="Potvrdit i tak"
                                isDark={isDark}
                            />
                        )}
                    </div>
                </div>
            )}

            {!zamekObjednavky && !zamekData && !zamekKolegove && dataServer?.text && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    fontSize: 11.5, fontWeight: 600, color: barvy.textMuted,
                }}>
                    <ShieldCheck size={13} color={barvy.success} style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                        {dataServer.text}
                        {cekaNaPlatbu > 0 && ` · ${cekaNaPlatbu}× čeká na online platbu (nepočítá se)`}
                    </span>
                    {onZnovu && <TlacitkoZnovu onZnovu={onZnovu} nacitam={nacitam} isDark={isDark} tiche />}
                </div>
            )}
        </div>
    );
}

function TlacitkoZnovu({ onZnovu, nacitam, isDark, tiche = false }) {
    const barvy = getTextColors(isDark);
    if (!onZnovu) return null;

    return (
        <button
            type="button"
            onClick={onZnovu}
            disabled={nacitam}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: tiche ? '2px 6px' : '7px 12px',
                borderRadius: SHEET_TOKENS.radius.button,
                border: tiche ? '1px solid transparent' : `1px solid ${barvy.divider}`,
                background: tiche ? 'transparent' : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                color: tiche ? barvy.textMuted : barvy.textPrimary,
                fontSize: tiche ? 11 : 12.5, fontWeight: 700,
                cursor: nacitam ? 'default' : 'pointer',
                opacity: nacitam ? 0.6 : 1,
                flexShrink: 0,
            }}
        >
            <RefreshCw size={tiche ? 12 : 14} style={nacitam ? { animation: 'spin 1s linear infinite' } : undefined} />
            {nacitam ? 'Kontroluji…' : (tiche ? 'Znovu' : 'Zkontrolovat znovu')}
        </button>
    );
}

/**
 * Vědomé přeskočení — ne zaškrtávátko schované v textu, ale tlačítko, které
 * po stisku drží zapnutý stav. Aby se to nestalo omylem a bylo na první
 * pohled vidět, že je zapnuté.
 */
function Prepinac({ zapnuto, onZmena, popis, isDark }) {
    const barvy = getTextColors(isDark);

    return (
        <button
            type="button"
            onClick={onZmena}
            aria-pressed={zapnuto}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '7px 12px',
                borderRadius: SHEET_TOKENS.radius.button,
                border: `1px solid ${zapnuto ? 'rgba(239,68,68,0.55)' : barvy.divider}`,
                background: zapnuto ? 'rgba(239,68,68,0.16)' : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                color: zapnuto ? barvy.danger : barvy.textSecondary,
                fontSize: 12.5, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
            }}
        >
            <span style={{
                width: 16, height: 16, borderRadius: 5, flexShrink: 0,
                border: `2px solid ${zapnuto ? barvy.danger : barvy.textMuted}`,
                background: zapnuto ? barvy.danger : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {zapnuto && <Check size={11} color="#fff" strokeWidth={3} />}
            </span>
            {popis}
        </button>
    );
}
